/**
 * MyLojic Color Palette
 *
 * This is the single source of truth for all colors in the application.
 * Never use hardcoded hex values in components - always reference these tokens.
 *
 * Usage in components:
 *   sx={{ bgcolor: 'background.card' }}        // Using MUI theme path
 *   sx={{ color: 'primary.main' }}             // Using MUI theme path
 *   sx={{ bgcolor: (theme) => theme.palette.background.card }}  // Using theme callback
 */

// Brand colors - the core identity
// v3.92 - Aligned to logo-v3 spec (navy #003366→#1B2A4A, added brightTeal/darkPrimary/darkTeal/errorRed) - 2026-03-19
export const brand = {
  navy: '#1B2A4A',        // Primary text, headings, sidebar (v3: was #003366)
  teal: '#008080',        // Primary actions, buttons, links, wordmark accent
  tealDark: '#006666',    // Hover states
  tealLight: '#00A0A0',   // Highlights, selected states
  brightTeal: '#00C9A7',  // Logo ring gradient, M gradient highlight (v3)
  orange: '#F4A261',      // CTA buttons, ring gradient accent
  orangeDark: '#E89251',  // CTA hover
  errorRed: '#E87461',    // Logo ring accent, error variant (v3)
  lightBlue: '#BBCDE2',   // Card backgrounds, highlights
  darkPrimary: '#0A1628', // Dark mode bg + mark fill (v3)
  darkTeal: '#081A1A',    // Dark solution page backgrounds (v3)
} as const;

// Primary palette (teal-based)
export const primary = {
  main: brand.teal,
  dark: brand.tealDark,
  light: brand.tealLight,
  contrastText: '#FFFFFF',
} as const;

// Secondary palette (navy-based)
export const secondary = {
  main: brand.navy,
  light: brand.lightBlue,
  dark: '#002244',
  contrastText: '#FFFFFF',
} as const;

// Accent palette (orange-based, for CTAs)
export const accent = {
  main: brand.orange,
  dark: brand.orangeDark,
  light: '#F6B88A',
  contrastText: '#FFFFFF',
} as const;

// Background colors
export const background = {
  default: '#F5F5F5',    // Page background
  paper: '#FFFFFF',      // Cards, modals, papers
  card: brand.lightBlue, // KPI cards, feature highlights
  sidebar: brand.navy,   // Sidebar background
} as const;

// Text colors
export const text = {
  primary: brand.navy,   // Main text
  secondary: '#666666',  // Muted text, descriptions
  disabled: '#999999',   // Disabled states
  onDark: '#FFFFFF',     // Text on dark backgrounds
  onPrimary: '#FFFFFF',  // Text on primary color
} as const;

// Status/feedback colors
export const status = {
  success: '#4CAF50',
  successLight: '#E8F5E9',
  warning: '#FF9800',
  warningLight: '#FFF3E0',
  error: '#F44336',
  errorLight: '#FFEBEE',
  info: '#2196F3',
  infoLight: '#E3F2FD',
} as const;

// Border colors
export const border = {
  light: '#E0E0E0',
  main: '#BDBDBD',
  dark: '#9E9E9E',
} as const;

// v2.92 - Dark mode palette
// v3.92 - Brand dark primary (#0A1628) for card/sidebar; v3.98 - background lift #111827/#1A2333 - 2026-03-20
// v4.01 - Further background lift: #1A2233 default / #243044 paper - 2026-03-20
export const darkBackground = {
  default: '#1A2233',     // Page background (v4.01: lifted blue-grey, more readable)
  paper: '#243044',       // Cards, modals, papers (v4.01: elevated layer above default)
  card: '#1A2D40',        // KPI cards, feature highlights
  sidebar: brand.darkPrimary, // Sidebar background — brand dark (#0A1628)
} as const;

export const darkText = {
  primary: '#E0E0E0',    // Main text
  secondary: '#B0B0B0',  // Muted text, descriptions
  disabled: '#666666',   // Disabled states
  onDark: '#FFFFFF',     // Text on dark backgrounds
  onPrimary: '#FFFFFF',  // Text on primary color
} as const;

export const darkBorder = {
  light: '#333333',
  main: '#444444',
  dark: '#555555',
} as const;

export const darkStatus = {
  success: '#66BB6A',
  successLight: '#1B3D1F',
  warning: '#FFA726',
  warningLight: '#3D2E14',
  error: '#EF5350',
  errorLight: '#3D1B1B',
  info: '#42A5F5',
  infoLight: '#142D3D',
} as const;

// Combined colors object for easy import
export const colors = {
  brand,
  primary,
  secondary,
  accent,
  background,
  text,
  status,
  border,
  darkBackground,
  darkText,
  darkBorder,
  darkStatus,
} as const;

export default colors;
